import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import userServices from "services/userServices";

const TranslationContext = createContext({
  t: (key, fallback) => fallback || key,
  language: "en",
  isReady: false,
  changeLanguage: () => {},
});

export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState(
    () => localStorage.getItem("language") || "en"
  );
  const [translations, setTranslations] = useState({});
  const [isReady, setIsReady] = useState(false);

  const fetchTranslations = useCallback(async (lang) => {
    try {
      setIsReady(false);

      const res = await userServices.getTranslations(lang);
      const data = res?.data?.data || {};

      setTranslations(data);
      setLanguage(lang);

      localStorage.setItem("language", lang);
      localStorage.setItem("translations", JSON.stringify(data));

      setIsReady(true);
    } catch (err) {
      console.error("[i18n] Failed to load translations", err);
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    const cachedLang = localStorage.getItem("language") || "en";
    const cachedTranslations = localStorage.getItem("translations");

    if (cachedTranslations) {
      const parsed = JSON.parse(cachedTranslations);
      setLanguage(cachedLang);
      setTranslations(parsed);
      setIsReady(true);
    } else {
      fetchTranslations(cachedLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLanguage = (lang) => {
    if (lang === language) return;
    fetchTranslations(lang);
  };

  const t = useMemo(() => {
    return (key, fallback = key) => {
      return translations[key] ?? fallback;
    };
  }, [translations]);

  return (
    <TranslationContext.Provider
      value={{ t, language, isReady, changeLanguage }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useContext(TranslationContext);
